import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowDownRight, Check, Ruler, Truck, Users } from "lucide-react";
import { areas, faqs, gallery, reviews, services } from "../data/content";
import { ChevronDivider, Eyebrow, FinalCta, MagneticLink, Reveal, TextReveal } from "./Primitives";

export function HomePage() {
  const reduceMotion = useReducedMotion();
  const hero = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: hero, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", reduceMotion ? "0%" : "12%"]);
  return (
    <>
      <section className="hero" ref={hero}>
        <motion.div className="hero-media" style={{ y: imageY }}>
          <video
            autoPlay={!reduceMotion}
            muted
            loop
            playsInline
            preload="metadata"
            poster="/images/gallery/hero-flooring-poster.jpg"
            aria-hidden="true"
          >
            <source src="/videos/hero-flooring-muted.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="hero-shade" />
        <div className="shell hero-frame">
        <div className="hero-copy">
          <motion.div initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Eyebrow>Independent flooring studio · Horncastle</Eyebrow>
          </motion.div>
          <TextReveal as="h1" text="Floors worth living on." accent={["living", "on."]} />
          <motion.p className="hero-intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.38 }}>
            Chosen in person. Measured at home. Fitted by people who care about every line, edge and threshold.
          </motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.55 }}>
            <MagneticLink to="/get-a-quote">Arrange your free measure <ArrowDownRight /></MagneticLink>
            <Link className="text-link light" to="/gallery">Explore real projects <span>↗</span></Link>
          </motion.div>
        </div>
        <motion.aside className="hero-proof" initial={{ opacity: 0, x: reduceMotion ? 0 : 22 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .65, duration: .7 }}>
          <span>01 / 03</span>
          <p>One local team from first sample to final trim.</p>
          <strong>100<small>%</small></strong>
          <p>Facebook recommendation record</p>
        </motion.aside>
        <div className="hero-caption"><span>Featured finish</span><p>Oak-look LVT · Lincolnshire</p></div>
        </div>
      </section>
      <TrustStrip />
      <Ticker />
      <ServicesSection />
      <FeaturedWork />
      <WhyUs />
      <Process />
      <ReviewsSection />
      <AreasStrip />
      <FaqPreview />
      <FinalCta />
    </>
  );
}

function TrustStrip() {
  const items = [[Users, "Family-run"], [Ruler, "Free measuring"], [Check, "100% recommend"], [Truck, "Local delivery"]];
  return <div className="trust-strip"><div className="shell">{items.map(([Icon, label]) => <span key={label as string}><Icon />{label as string}</span>)}</div></div>;
}

function Ticker() {
  const text = "CARPETS · LVT · LAMINATE · VINYL · HERRINGBONE · ARTIFICIAL GRASS · HORNCASTLE · LOUTH · WOODHALL SPA · ";
  return <div className="ticker" aria-label="Flooring services and areas"><div><span>{text}</span><span aria-hidden="true">{text}</span></div></div>;
}

function ServicesSection() {
  const [active, setActive] = useState(0);
  return (
    <section className="section services-section" id="materials">
      <div className="shell services-intro">
        <div><Eyebrow>Six materials. One exacting standard.</Eyebrow><h2>Start with how the room needs to work.</h2></div>
        <p>We’ll weigh up wear, warmth, preparation and price with you. The best floor is the one that still makes sense after the samples go away.</p>
      </div>
      <div className="shell service-ledger">
        <ol>
          {services.map((service, index) => (
            <li key={service.slug} className={active === index ? "active" : ""} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)}>
              <Link to={`/services/${service.slug}`}>
                <span>0{index + 1}</span><h3>{service.shortName}</h3><p>{service.kicker}</p><ArrowDownRight />
              </Link>
            </li>
          ))}
        </ol>
        <div className="service-stage" aria-live="polite">
          {services.map((service, index) => <img key={service.slug} className={active === index ? "active" : ""} src={service.image} alt={active === index ? service.alt : ""} />)}
          <div><span>{services[active].price}</span><Link to="/services">Compare every floor ↗</Link></div>
        </div>
      </div>
    </section>
  );
}

function FeaturedWork() {
  const featured = gallery.filter((item) => item.featured).slice(0, 5);
  return (
    <section className="section featured-section">
      <ChevronDivider />
      <div className="shell featured-intro">
        <div><Eyebrow>From the fitting diary</Eyebrow><h2>Real rooms. Look at the edges.</h2></div>
        <div className="featured-intro-note"><span>05 recent finishes</span><p>No stock photography and no imaginary makeovers. These are Lincolnshire floors, fitted by the people you’ll meet.</p><Link className="text-link" to="/gallery">Open the fitting archive <span>↗</span></Link></div>
      </div>
      <div className="featured-grid">
        {featured.map((item, index) => (
          <Reveal className={`featured-item featured-${index + 1}`} key={item.id} delay={index * 0.06}>
            <Link to="/gallery" aria-label={`${item.caption} — view the fitting gallery`}>
              <img src={item.image} alt={item.alt} loading="lazy" decoding="async" />
              <div><span>{item.category}</span><p>{item.caption}</p><small>View project ↗</small></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const whyItems = [
  { title: "We say what we see", text: "If preparation is wrong—or another job has failed—we’ll explain why in plain English and what can genuinely be fixed.", image: "/images/gallery/fb_image_12_20260730.jpg", alt: "Herringbone floor fitted around an oak staircase" },
  { title: "Fair means transparent", text: "Your quote sets out the product, preparation and fitting. No mysterious extras after the floor is lifted.", image: "/images/gallery/fb_image_2_20260730.jpg", alt: "Carefully fitted kitchen vinyl" },
  { title: "The details stay tidy", text: "Door bars, corners, stairs and pattern lines are where a fitting shows its quality. We take our time with them.", image: "/images/gallery/fb_image_6_20260730.jpg", alt: "Bound stair runner showing precise edges" },
  { title: "Local has its advantages", text: "You can see ranges in person, get a free measure and speak to the same independent team throughout.", image: "/images/gallery/storefront.jpg", alt: "Horncastle Flooring showroom" },
];

function WhyUs() {
  const section = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!section.current || reduceMotion) return;
    gsap.registerPlugin(ScrollTrigger);
    const blocks = gsap.utils.toArray<HTMLElement>(".why-copy-block", section.current);
    const triggers = blocks.map((block, index) => ScrollTrigger.create({
      trigger: block,
      start: "top 52%",
      end: "bottom 48%",
      onEnter: () => setActive(index),
      onEnterBack: () => setActive(index),
    }));
    return () => triggers.forEach((trigger) => trigger.kill());
  }, [reduceMotion]);
  return (
    <section className="section why-section" ref={section}>
      <div className="shell why-grid">
        <div className="why-sticky">
          <Eyebrow>Why Horncastle Flooring</Eyebrow>
          <h2>We stand behind every detail.</h2>
          <div className="why-image-stack">
            {whyItems.map((item, index) => <img key={item.title} className={active === index ? "active" : ""} src={item.image} alt={item.alt} loading="lazy" decoding="async" />)}
            <span className="why-number">0{active + 1}</span>
          </div>
        </div>
        <div className="why-copy">
          {whyItems.map((item, index) => <article className={`why-copy-block ${active === index ? "active" : ""}`} key={item.title} onMouseEnter={() => setActive(index)}><span>0{index + 1}</span><img className="why-mobile-image" src={item.image} alt="" aria-hidden="true" loading="lazy" decoding="async" /><h3>{item.title}</h3><p>{item.text}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    ["01", "Tell us about the room", "Call, WhatsApp or send the short form. A photo is useful, but it is not essential."],
    ["02", "We measure for free", "We check sizes, levels, thresholds and the preparation your floor will need."],
    ["03", "Choose with confidence", "Compare samples in the right light, then receive one clear written quote."],
    ["04", "We fit and finish", "Your local fitting team handles the details and keeps you updated throughout."],
  ];
  return (
    <section className="section process-section">
      <div className="shell process-layout">
        <div className="process-heading">
          <Eyebrow>From first look to final trim</Eyebrow>
          <h2 className="text-reveal">A simple plan for getting it right.</h2>
          <p>The free measure gives us the detail to price the whole room properly before you commit.</p>
          <Link className="text-link" to="/get-a-quote">Arrange your free measure <span>↗</span></Link>
        </div>
        <div className="process-plan">
          <div className="process-plan-head"><span>Your flooring plan</span><small>Four clear steps · one local team</small></div>
          <ol className="process-grid">
            {steps.map(([num, title, text]) => (
              <li key={num}>
                <span>{num}</span>
                <div><h3 className="text-reveal">{title}</h3><p>{text}</p></div>
                <ArrowDownRight aria-hidden="true" />
              </li>
            ))}
          </ol>
          <div className="process-plan-foot"><span>01</span><i /><strong>Ready when you are</strong><span>04</span></div>
        </div>
      </div>
    </section>
  );
}

function ReviewsSection() {
  return (
    <section className="section reviews-home">
      <div className="shell reviews-editorial">
        <div className="review-score"><strong>100<small>%</small></strong><span>Facebook recommendation record</span></div>
        <article className="lead-review"><div className="quote-mark">“</div><blockquote>{reviews[1].quote}</blockquote><footer>{reviews[1].name} · {reviews[1].source}</footer></article>
        <div className="review-notes">{reviews.filter((_, index) => index !== 1).map((review) => <article key={review.quote}><blockquote>“{review.quote}”</blockquote><footer><strong>{review.name}</strong><span>{review.source}</span></footer></article>)}</div>
        <Link className="text-link" to="/reviews">What customers notice <span>↗</span></Link>
      </div>
    </section>
  );
}

function AreasStrip() {
  return (
    <section className="areas-section">
      <div className="shell areas-inner"><div><Eyebrow>Measured from Horncastle</Eyebrow><h2>Seven places. One local team.</h2><p>Based on Wharf Road and fitting throughout the Wolds, Lindsey and beyond.</p></div><nav aria-label="Areas served">{areas.map(([slug, name], index) => <Link key={slug} to={`/areas/${slug}`}><small>0{index + 1}</small>{name}<span>↗</span></Link>)}</nav></div>
    </section>
  );
}

function FaqPreview() {
  return (
    <section className="section faq-preview">
      <div className="shell faq-grid"><div><Eyebrow>Ask the practical questions</Eyebrow><h2>What needs checking before anything is ordered?</h2><p>Preparation, pricing and fitting in plain English.</p><Link className="button button-outline" to="/faq">Read every answer</Link></div><div className="accordion-list">{faqs.slice(0, 4).map(([q, a], i) => <details key={q} open={i === 0}><summary><span>0{i + 1}</span>{q}<b>+</b></summary><p>{a}</p></details>)}</div></div>
    </section>
  );
}
